import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "container",
    "sectionCard",
    "form",
    "option",
    "optionLabel",
    "textInput",
    "submitButton",
    "keyboardModal",
    "detailsToggle",
    "detailsSection"
  ]

  static values = {
    attemptId: Number,
    selectedOption: { type: Number, default: -1 }
  }

  connect() {
    this.preventBackNavigation()
    this.setupKeyboardNavigation()
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleKeydown)
  }

  preventBackNavigation() {
    history.replaceState(null, "", window.location.href)
    window.addEventListener("popstate", () => {
      history.pushState(null, "", window.location.href)
    })
  }

  setupKeyboardNavigation() {
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundHandleKeydown)
  }

  handleKeydown(event) {
    // Ignore if user is typing in text input
    if (this.hasTextInputTarget && document.activeElement === this.textInputTarget) {
      if (event.key === "Enter") {
        event.preventDefault()
        this.submitForm()
      }
      return
    }

    // Ignore if modal is open
    if (this.hasKeyboardModalTarget && !this.keyboardModalTarget.classList.contains("hidden")) {
      if (event.key === "Escape") {
        this.hideKeyboardHelp()
      }
      return
    }

    switch (event.key) {
      case "1":
      case "2":
      case "3":
      case "4":
        event.preventDefault()
        this.selectOptionByNumber(parseInt(event.key) - 1)
        break
      case "Enter":
        event.preventDefault()
        this.submitForm()
        break
      case "?":
        event.preventDefault()
        this.showKeyboardHelp()
        break
      case "Escape":
        this.hideKeyboardHelp()
        break
    }
  }

  selectOptionByNumber(index) {
    if (!this.hasOptionTarget) return

    const options = this.optionTargets
    if (index >= 0 && index < options.length) {
      options[index].checked = true
      options[index].dispatchEvent(new Event("change", { bubbles: true }))
      this.selectedOptionValue = index
      this.updateOptionLabels()
    }
  }

  selectOption(event) {
    const option = event.currentTarget
    const index = this.optionTargets.indexOf(option)
    this.selectedOptionValue = index
    this.updateOptionLabels()
  }

  updateOptionLabels() {
    if (!this.hasOptionLabelTarget) return

    this.optionLabelTargets.forEach((label, index) => {
      if (index === this.selectedOptionValue) {
        label.classList.add("border-indigo-500", "bg-indigo-50")
        label.classList.remove("border-gray-200")
      } else {
        label.classList.remove("border-indigo-500", "bg-indigo-50")
        label.classList.add("border-gray-200")
      }
    })
  }

  submitAnswer(event) {
    event.preventDefault()

    // Validate that an answer is selected
    if (!this.validateAnswer()) {
      this.showValidationError()
      return
    }

    this.setLoadingState()
    this.formTarget.requestSubmit()
  }

  submitForm() {
    if (!this.hasFormTarget) return

    if (!this.validateAnswer()) {
      this.showValidationError()
      return
    }

    this.setLoadingState()
    this.formTarget.requestSubmit()
  }

  validateAnswer() {
    return this.validateSection()
  }

  validateSection() {
    const questionContainers = this.element.querySelectorAll(".question-container")
    let allValid = true
    let firstInvalid = null

    questionContainers.forEach(container => {
      const radioInputs = container.querySelectorAll('input[type="radio"]')
      const textInput = container.querySelector('input[type="text"]')

      let isValid = false

      if (radioInputs.length > 0) {
        isValid = Array.from(radioInputs).some(radio => radio.checked)
      } else if (textInput) {
        isValid = textInput.value.trim() !== ""
      }

      if (!isValid) {
        allValid = false
        if (!firstInvalid) {
          firstInvalid = container
        }
        container.classList.add("ring-2", "ring-red-300", "rounded-lg")
      } else {
        container.classList.remove("ring-2", "ring-red-300", "rounded-lg")
      }
    })

    if (!allValid && firstInvalid) {
      firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" })
    }

    return allValid
  }

  showValidationError() {
    if (this.hasSectionCardTarget) {
      this.sectionCardTarget.classList.add("animate-shake")
      setTimeout(() => {
        this.sectionCardTarget.classList.remove("animate-shake")
      }, 500)
    }

    const firstUnanswered = this.element.querySelector(".question-container.ring-2")
    if (firstUnanswered) {
      const input = firstUnanswered.querySelector('input[type="radio"], input[type="text"]')
      if (input) input.focus()
    }
  }

  setLoadingState() {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.innerHTML = `
      <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <span>Chargement...</span>
    `
  }

  showKeyboardHelp() {
    if (!this.hasKeyboardModalTarget) return
    this.keyboardModalTarget.classList.remove("hidden")
    this.keyboardModalTarget.classList.add("flex")
  }

  hideKeyboardHelp() {
    if (!this.hasKeyboardModalTarget) return
    this.keyboardModalTarget.classList.add("hidden")
    this.keyboardModalTarget.classList.remove("flex")
  }

  toggleDetails() {
    if (!this.hasDetailsSectionTarget) return

    const isHidden = this.detailsSectionTarget.classList.contains("hidden")

    if (isHidden) {
      this.detailsSectionTarget.classList.remove("hidden")
      if (this.hasDetailsToggleTarget) {
        this.detailsToggleTarget.innerHTML = `
          <i class="fa-solid fa-chevron-up mr-2" aria-hidden="true"></i>
          Masquer le détail
        `
      }
    } else {
      this.detailsSectionTarget.classList.add("hidden")
      if (this.hasDetailsToggleTarget) {
        this.detailsToggleTarget.innerHTML = `
          <i class="fa-solid fa-list-check mr-2" aria-hidden="true"></i>
          Voir le détail
        `
      }
    }
  }

  toggleExplanation(event) {
    const explanationId = event.currentTarget.dataset.explanationId
    const explanationEl = document.getElementById(explanationId)

    if (explanationEl) {
      explanationEl.classList.toggle("hidden")
    }
  }
}
